import { defineStore } from 'pinia';
import i18n from '@/i18n';
import appSetting from '@/app-setting';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { useAuthStore } from '@/stores/auth';
import { useBranchesStore } from '@/stores/branches';

export const useAppStore = defineStore('app', {
    state: () => ({
        isDarkMode: false,
        mainLayout: null,
        theme: 'light',
        menu: 'vertical',
        layout: 'full',
        rtlClass: 'rtl',
        animation: '',
        navbar: 'navbar-sticky',
        locale: 'ae',
        sidebar: false,
        languageList: [
            { code: 'en', name: 'English' },
            { code: 'ae', name: 'Arabic' }
        ],
        isShowMainLoader: true,
        showInnerLoader: true,
        semidark: false,
        viewDashboardNumbers: (localStorage.getItem('view_dashboard_numbers') || '1') == 1,
        navbarButtons: [],
        showAddCustomerOrder: false,
        showAddMerchantOrder: false,
        showAddOtherOrder: false,
        showAddBranchOrder: false,
        showAddCustomer: null,
        editCustomerData: null,
        customerAddedCallback: null,
        helpButton: null,
        showGoldPricesBar: (localStorage.getItem('goldPricesBar') || '1') === '1'
    }),

    actions: {
        setMainLayout(payload: any = null) {
            this.mainLayout = payload; //app , auth
        },
        toggleTheme(payload: any = null) {
            payload = payload || this.theme; // light|dark|system
            localStorage.setItem('theme', payload);
            this.theme = payload;
            if (payload == 'light') {
                this.isDarkMode = false;
            } else if (payload == 'dark') {
                this.isDarkMode = true;
            } else if (payload == 'system') {
                if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
                    this.isDarkMode = true;
                } else {
                    this.isDarkMode = false;
                }
            }

            if (this.isDarkMode) {
                document.querySelector('body')?.classList.add('dark');
            } else {
                document.querySelector('body')?.classList.remove('dark');
            }
        },
        toggleMenu(payload: any = null) {
            payload = payload || this.menu; // vertical, collapsible-vertical, horizontal
            this.sidebar = false; // reset sidebar state
            localStorage.setItem('menu', payload);
            this.menu = payload;
        },
        toggleLayout(payload: any = null) {
            payload = payload || this.layout; // full, boxed-layout
            localStorage.setItem('layout', payload);
            this.layout = payload;
        },
        toggleRTL(payload: any = null) {
            payload = payload || this.rtlClass; // rtl, ltr
            localStorage.setItem('rtlClass', payload);
            this.rtlClass = payload;
            document.querySelector('html')?.setAttribute('dir', this.rtlClass || 'ltr');
        },
        toggleAnimation(payload: any = null) {
            payload = payload || this.animation; // animate__fadeIn, animate__fadeInDown, animate__fadeInUp, animate__fadeInLeft, animate__fadeInRight, animate__slideInDown, animate__slideInLeft, animate__slideInRight, animate__zoomIn
            payload = payload?.trim();
            localStorage.setItem('animation', payload);
            this.animation = payload;
            appSetting.changeAnimation();
        },
        toggleNavbar(payload: any = null) {
            payload = payload || this.navbar; // navbar-sticky, navbar-floating, navbar-static
            localStorage.setItem('navbar', payload);
            this.navbar = payload;
        },
        toggleSemidark(payload: any = null) {
            payload = payload || false;
            localStorage.setItem('semidark', payload);
            this.semidark = payload;
        },
        toggleLocale(payload: any = null) {
            payload = payload || this.locale;
            i18n.global.locale.value = payload;
            localStorage.setItem('i18n_locale', payload);
            this.locale = payload;
            if (this.locale?.toLowerCase() === 'ae') {
                this.toggleRTL('rtl');
            } else {
                this.toggleRTL('ltr');
            }
        },
        toggleSidebar(state: boolean = false) {
            this.sidebar = !this.sidebar;
        },
        toggleMainLoader(state: boolean = false) {
            this.isShowMainLoader = true;
            setTimeout(() => {
                this.isShowMainLoader = false;
            }, 500);
        },
        setViewDashboardNumbers(view: Boolean) {
            localStorage.setItem('view_dashboard_numbers', view ? 1 : 0);

            this.viewDashboardNumbers = view;
        },
        formatDate(date: any, withTime: boolean = false) {
            const branchesStore = useBranchesStore();

            return format(date, (withTime ? 'hh:mm - ' : '') + (branchesStore.currentBranch.date_format || 'dd LLL yyyy'), { locale: ar });
        },
        closeAddNewCustomer(refreshTable: string | null = null, customer: Object | null = null) {
            this.showAddCustomer = null;

            this.editCustomerData = null;

            if (this.customerAddedCallback !== null) {
                this.customerAddedCallback(refreshTable, customer);
            }
        },
        toggleNavbarButton(button: string) {
            if (this.navbarButtons.find(b => b === button)) {
                this.navbarButtons = this.navbarButtons.filter(b => b !== button);
            } else {
                this.navbarButtons.push(button);
            }

            const authStore = useAuthStore();

            authStore.request(false).put('/api/user', { navbar_buttons: this.navbarButtons });
        },
        toggleGoldPricesBar() {
            this.showGoldPricesBar = !this.showGoldPricesBar;

            localStorage.setItem('goldPricesBar', this.showGoldPricesBar ? '1' : '0');
        }
    },
    getters: {}
});
